/**
 * UserRolePage — User, Role & Permission Management
 *
 * Lists all tenant members and allows admins to:
 *   - Change a member's primary role
 *   - Add or remove explicit grants (extra permissions on top of role)
 *
 * Roles (change requires: admin):
 *   admin | manager | supervisor | staff | driver | warehouse | member | viewer
 *
 * Grants (change requires: admin | manager):
 *   e.g. "ledger:read", "finance:read" — stored in tenant_employees.permissions.grants
 *
 * Route: /admin/users  (admin/manager only)
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  Loader2,
  RefreshCw,
  Pencil,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Types ─────────────────────────────────────────────────────────────────

interface Member {
  userId: string;
  email: string;
  name: string | null;
  role: string;
  grants: string[];
  joinedAt: string;
}

// ─── Constants (fallback defaults — runtime roles derived from tenant data) ──

const DEFAULT_ROLES = [
  'admin', 'manager', 'supervisor', 'staff', 'driver', 'warehouse', 'member', 'viewer',
] as const;

const ROLE_COLORS: Record<string, string> = {
  admin:          'bg-red-100 text-red-800 border-red-200',
  manager:        'bg-orange-100 text-orange-800 border-orange-200',
  supervisor:     'bg-yellow-100 text-yellow-800 border-yellow-200',
  staff:          'bg-blue-100 text-blue-800 border-blue-200',
  driver:         'bg-cyan-100 text-cyan-800 border-cyan-200',
  warehouse:      'bg-teal-100 text-teal-800 border-teal-200',
  member:         'bg-slate-100 text-slate-700 border-slate-200',
  viewer:         'bg-gray-100 text-gray-600 border-gray-200',
  platform_admin: 'bg-purple-100 text-purple-800 border-purple-200',
};

// Common permission suggestions (fallback — can be extended by tenant config)
const DEFAULT_PERM_SUGGESTIONS = [
  'job:read', 'job:write', 'trip:read', 'trip:write',
  'customer:read', 'customer:write', 'ledger:read', 'ledger:write',
  'shipment:read', 'document:read', 'conversation:read',
  'agent:read', 'warehouse:read', 'warehouse:write',
  'finance:read', 'finance:write', 'tax:read', 'config:read', 'report:read',
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function UserRolePage() {
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin' || authUser?.role === 'platform_admin';
  const canManage = isAdmin || authUser?.role === 'manager';

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Edit dialog state
  const [editTarget, setEditTarget] = useState<Member | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editGrantsStr, setEditGrantsStr] = useState('');
  const [newGrant, setNewGrant] = useState('');
  const [savingRole, setSavingRole] = useState(false);
  const [savingGrants, setSavingGrants] = useState(false);

  // API-loaded roles and permissions (replaces pure hardcoded lists)
  const [apiRoles, setApiRoles] = useState<string[]>([]);
  const [apiPerms, setApiPerms] = useState<string[]>([]);

  // Derive roles: API is primary source; built-in defaults only when API returned nothing
  const ROLES = useMemo(() => {
    const base = apiRoles.length > 0 ? apiRoles : [...DEFAULT_ROLES];
    const memberRoles = members.map((m) => m.role).filter(Boolean);
    const all = new Set<string>([...base, ...memberRoles]);
    return Array.from(all);
  }, [members, apiRoles]);

  // Derive permissions: API is primary source; built-in defaults only when API returned nothing
  const PERM_SUGGESTIONS = useMemo(() => {
    const base = apiPerms.length > 0 ? apiPerms : [...DEFAULT_PERM_SUGGESTIONS];
    const memberGrants = members.flatMap((m) => m.grants ?? []);
    const all = new Set<string>([...base, ...memberGrants]);
    return Array.from(all).sort();
  }, [members, apiPerms]);

  // ── Load roles & permissions from API ─────────────────────────────────────
  useEffect(() => {
    api.get<Array<{ id: string; name: string }>>('/admin/roles')
      .then((rows) => setApiRoles(rows.map((r) => r.name)))
      .catch(() => { /* defaults suffice */ });
    api.get<Array<{ id: string }>>('/admin/permissions')
      .then((rows) => setApiPerms(rows.map((p) => p.id)))
      .catch(() => { /* defaults suffice */ });
  }, []);

  // ── Load members ──────────────────────────────────────────────────────────
  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Member[]>('/admin/members');
      setMembers(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load members';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadMembers(); }, [loadMembers]);

  // ── Open edit dialog ──────────────────────────────────────────────────────
  const openEdit = (m: Member) => {
    setEditTarget(m);
    setEditRole(m.role);
    setEditGrantsStr(m.grants.join(', '));
    setNewGrant('');
  };

  // ── Parse grants from comma string ───────────────────────────────────────
  const parseGrants = (s: string) =>
    s.split(',').map((g) => g.trim()).filter(Boolean);

  const currentGrants = parseGrants(editGrantsStr);

  const addGrant = (g: string) => {
    const trimmed = g.trim();
    if (!trimmed || currentGrants.includes(trimmed)) return;
    setEditGrantsStr(currentGrants.concat(trimmed).join(', '));
    setNewGrant('');
  };

  const removeGrant = (g: string) => {
    setEditGrantsStr(currentGrants.filter((x) => x !== g).join(', '));
  };

  // ── Save role ─────────────────────────────────────────────────────────────
  const saveRole = async () => {
    if (!editTarget || editRole === editTarget.role) return;
    setSavingRole(true);
    try {
      await api.patch(`/admin/members/${editTarget.userId}/role`, { role: editRole });
      setMembers((prev) => prev.map((m) => m.userId === editTarget.userId ? { ...m, role: editRole } : m));
      setEditTarget((prev) => prev ? { ...prev, role: editRole } : null);
      toast.success(`Role updated to ${editRole}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  // ── Save grants ───────────────────────────────────────────────────────────
  const saveGrants = async () => {
    if (!editTarget) return;
    const grants = parseGrants(editGrantsStr);
    setSavingGrants(true);
    try {
      await api.patch(`/admin/members/${editTarget.userId}/grants`, { grants });
      setMembers((prev) => prev.map((m) => m.userId === editTarget.userId ? { ...m, grants } : m));
      setEditTarget((prev) => prev ? { ...prev, grants } : null);
      toast.success('Grants updated');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update grants');
    } finally {
      setSavingGrants(false);
    }
  };

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = search.trim()
    ? members.filter((m) =>
        m.email.toLowerCase().includes(search.toLowerCase()) ||
        (m.name ?? '').toLowerCase().includes(search.toLowerCase()),
      )
    : members;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Users & Permissions
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage member roles and extra grants for this tenant.
            {' '}<strong>Role</strong> sets broad permissions.{' '}
            <strong>Grants</strong> add individual permissions on top of the role.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadMembers} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Access note */}
      {!canManage && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          You have read-only access. Only admins and managers can modify roles and grants.
        </div>
      )}

      {/* Permission model reference */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            Role Permissions Reference
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 text-xs">
            {ROLES.map((r) => (
              <div key={r} className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${ROLE_COLORS[r] ?? 'bg-muted'}`}>
                {r}
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            admin → all · manager → all ops · supervisor → job/trip/warehouse · staff → job/trip/customer ·
            driver → trip only · warehouse → warehouse only · member → read-all · viewer → readonly
          </p>
        </CardContent>
      </Card>

      {/* Search + table */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base">Members ({filtered.length})</CardTitle>
          <Input
            className="h-8 w-56 text-sm"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground p-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading members…
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-destructive p-6">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Extra Grants</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No members found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((m) => (
                    <TableRow key={m.userId}>
                      {/* User */}
                      <TableCell>
                        <div className="font-medium text-sm">{m.name ?? m.email}</div>
                        {m.name && <div className="text-xs text-muted-foreground">{m.email}</div>}
                      </TableCell>

                      {/* Role */}
                      <TableCell>
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[m.role] ?? 'bg-muted'}`}>
                          {m.role}
                        </span>
                      </TableCell>

                      {/* Grants */}
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {m.grants.length === 0 ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            m.grants.map((g) => (
                              <Badge key={g} variant="outline" className="text-xs font-mono">
                                {g}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        {canManage && (
                          <Button variant="ghost" size="sm" onClick={() => openEdit(m)}>
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      {editTarget && (
        <Dialog open onOpenChange={() => setEditTarget(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Edit: {editTarget.name ?? editTarget.email}
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-5 py-2">
              {/* Role */}
              <div className="flex flex-col gap-2">
                <Label>Primary Role</Label>
                <div className="flex items-center gap-3">
                  <Select value={editRole} onValueChange={setEditRole} disabled={!isAdmin}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium mr-2 ${ROLE_COLORS[r] ?? ''}`}>
                            {r}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={saveRole}
                    disabled={savingRole || editRole === editTarget.role || !isAdmin}
                  >
                    {savingRole ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  </Button>
                </div>
                {!isAdmin && (
                  <p className="text-xs text-muted-foreground">Role changes require admin access.</p>
                )}
              </div>

              {/* Grants */}
              <div className="flex flex-col gap-2">
                <Label>Extra Grants</Label>
                <p className="text-xs text-muted-foreground">
                  Additional permissions on top of the role. User's next login will pick these up.
                </p>

                {/* Current grants */}
                <div className="flex flex-wrap gap-1.5 min-h-6">
                  {currentGrants.length === 0 ? (
                    <span className="text-xs text-muted-foreground">No extra grants</span>
                  ) : (
                    currentGrants.map((g) => (
                      <span
                        key={g}
                        className="inline-flex items-center gap-1 text-xs font-mono bg-muted rounded px-2 py-0.5 border"
                      >
                        {g}
                        <button
                          className="text-muted-foreground hover:text-destructive ml-0.5"
                          onClick={() => removeGrant(g)}
                          type="button"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>

                {/* Add grant input */}
                <div className="flex gap-2">
                  <Input
                    className="flex-1 h-8 text-xs font-mono"
                    placeholder="e.g. ledger:read"
                    value={newGrant}
                    onChange={(e) => setNewGrant(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { addGrant(newGrant); } }}
                    list="perm-suggestions"
                  />
                  <Button size="sm" variant="outline" onClick={() => addGrant(newGrant)} disabled={!newGrant.trim()}>
                    Add
                  </Button>
                  <datalist id="perm-suggestions">
                    {PERM_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
                  </datalist>
                </div>

                <Button
                  size="sm"
                  onClick={saveGrants}
                  disabled={savingGrants}
                  className="self-start"
                >
                  {savingGrants ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                  )}
                  Save Grants
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditTarget(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
